import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl,
    };
};
interface OwnProps {
    carouselType: string;
    product: CartLineModel | ProductContextModel;
    extendedStyles?: ProductImageStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;


export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            border: 1px solid #ececec;
            overflow: hidden;
            @media print {
                display: flex !important;
            }
            @media screen and (max-width: 1399px) {
                max-width: 9.6875rem;
                max-height: 9.6875rem;
            }
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: 100%;
            width: 100%;
            margin: 0 auto;
            max-width: 9.6875rem;
            max-height: 9.6875rem;
        `,
    },
};

const ProductImage: FC<Props> = ({ carouselType, product, extendedStyles, category, currentPageUrl }) => {

    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }
    altText += ` ${translate("redirect to product page")}`;

    const imagePath =
        "product" in product
            ? product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? "product.description" : "");

    const sku = "product" in product ? product.product.productNumber : "erpNumber" in product ? product.erpNumber : "";
    const position = "product" in product ? product.idx! + 1 : "line" in product ? product.line : 0;
    const condition = "product" in product ? product.product.properties.condition : "line" in product ? product.properties.cartLineCondition : "";
    

    const productClickDataLayerEvent = () => {
        let listName = "";
        if (carouselType !== undefined && category?.shortDescription !== undefined) {
            listName = carouselType;
        } else if (carouselType !== undefined && category?.shortDescription === undefined) {
            listName = carouselType;
        } else if (carouselType === undefined && category?.shortDescription !== undefined) {
            listName = "Category Results";
        }

        listName = currentPageUrl.includes("Search")
            ? "Search Results"
            : currentPageUrl.includes("Cart")
                ? "Cart Lines"
                : currentPageUrl.includes("category/All/")
                    ? "Brand Results"
                    : currentPageUrl.includes("category/all")
                        ? "Brand Results"
                        : currentPageUrl.includes("SavedOrders")
                            ? "Saved Order Details Page"
                            : currentPageUrl.includes("Orders/Details")
                                ? "Order History Details"
                                : currentPageUrl.includes("MyLists")
                                    ? "Product Wish List"
                                    : currentPageUrl.includes("CheckoutReviewAndSubmit")
                                        ? "Order Review"
                                        : currentPageUrl.includes("OrderConfirmation")
                                            ? "Order Confirmation"
                                            : listName;


        const dataLayer = (window as any).dataLayer || [];
        // dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        dataLayer.push({
            event: "productClick",
            ecommerce: {
                click: {
                    actionField: { list: listName, "action": "click" }, // Optional list property.                    
                    products: [
                        {
                            name: description, // Name or ID is required.
                            // id: "product" in product ? product.product.productNumber : "",
                            id: sku,
                            price:
                                "pricing" in product
                                    ? product.pricing?.actualPrice
                                    : product.productInfo.pricing?.actualPrice,

                            brand: "product" in product ? product.product.brand?.name : product.brand?.name,
                            condition,
                            category: category?.shortDescription ?? "",
                            stock:
                                "product" in product
                                    ? (
                                        //Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
                                        (product.productInfo?.inventory?.qtyOnHand!=undefined && product.productInfo?.inventory?.qtyOnHand<=0 
                                            && product.product.properties.isDirectShipFromManufacturer.toLowerCase()=="true")
                                        ? "DirectShip" :
                                        (product.productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                                            o =>
                                                o.unitOfMeasure.toLowerCase() ===
                                                (product.productInfo?.unitOfMeasure?.toLowerCase() || ""),
                                        )?.availability || undefined
                                        )?.message
                                    )
                                    : "",
                            stockqty: "product" in product ? product.productInfo.inventory?.qtyOnHand : 0,
                            // position: "product" in product ? product.idx! + 1 : 0,
                            position,
                        },
                    ],
                },
            },
        });
    };

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    return (
        <Clickable {...styles.linkWrappingImage}
            href={productDetailPath}
            data-test-selector="productImage"
            onClick={productClickDataLayerEvent}
        >
            <Img {...styles.img} loading={loading} src={imagePath} altText={altText} />
        </Clickable>
    );
};

export default connect(mapStateToProps)(ProductImage);